import React, { memo, useLayoutEffect, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import apiClient from '@miroculus/api-client';
import { Button, Input } from '@miroculus/nucleo';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { AuthPage } from 'components';
import {
  DASHBOARD_URL,
  FORGOT_PASSWORD_URL,
  SIGNUP_URL,
  CREATE_ORGANIZATION_URL,
} from 'cons/routes';
import { login, loginWithPassword } from 'reduxModules/auth';
import { getLogged } from 'reduxModules/auth/selectors';
import { useQuery } from 'hooks';
import { getRedirectUrl } from 'utils';
import { canSubmit, getFirstError, getFieldError } from 'utils/forms';
import styles from './Login.scss';

const cx = classnames.bind(styles);

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('The email you entered is invalid')
    .required('An email is required'),
  password: Yup.string().required('A password is required'),
});

const dataFromQuery = (query) => ({
  error: query.get('error'),
  email: query.get('email'),
  inviteToken: query.get('inviteToken'),
  jwt: query.get('jwt'),
  name: query.get('name'),
  refresh: query.get('refresh'),
  redirectTo: query.get('redirectTo'),
});

const processErrorFromQuery = (error) => {
  switch (error) {
    case 'INVALID_INVITE_TOKEN':
      toast.error('This invitation is invalid, please ask for another one.');
      break;
    case 'SERVER_ERROR':
      toast.error('There was a server error, please try again.');
      break;
    case 'INVALID_INVITE_EMAIL':
      toast.error("Your email doesn't match the invitation.");
      break;
    case 'MISSING_INVITE_TOKEN':
      toast.error('You need an invitation to login.');
      break;
    case 'EXPIRED_INVITE':
      toast.error('Your invitation expired, please ask for another one.');
      break;
    case 'INVALID_ORGANIZATION':
      toast.error('Your email is not from a valid organization.');
      break;
    default:
      toast.error(`Code: ${error}`);
      break;
  }
};

const getInviteMessage = (inviteToken, redirectTo) => {
  if (inviteToken) {
    return 'Please sign in to accept the team invitation.';
  }

  if (redirectTo?.includes(CREATE_ORGANIZATION_URL)) {
    return 'Please sign in to create a new organization.';
  }

  return null;
};

export function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const logged = useSelector(getLogged);

  const { error, email, inviteToken, jwt, name, refresh, redirectTo } =
    dataFromQuery(useQuery());

  const handleLoggedIn = useCallback(
    (redirectUrl) => {
      navigate(getRedirectUrl(redirectUrl, DASHBOARD_URL));
    },
    [navigate]
  );

  const handleLoginFromQuery = useCallback(
    async (data) => {
      await dispatch(login(data));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(
    async (data) => {
      await dispatch(loginWithPassword(data));
    },
    [dispatch]
  );

  const handleGoogleLogin = useCallback(
    (token, redirectUrl) => {
      const baseUrl = apiClient.getGoogleAuthUrl({
        redirectUrl:
          window.location.origin + getRedirectUrl(redirectUrl, DASHBOARD_URL),
      });
      const url = token ? `${baseUrl}&inviteToken=${token}` : baseUrl;
      window.location.replace(url);
    },
    []
  );

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      email: email ?? '',
      password: '',
    },
    onSubmit: async (values, actions) => {
      actions.setStatus(undefined);
      try {
        await handleSubmit({
          ...values,
          inviteToken,
        });
      } catch (e) {
        if (e.status === 400) {
          actions.setStatus(e.message);
        } else {
          throw e;
        }
      }
    },
    validationSchema: LoginSchema,
  });

  const onGoogleLoginClick = useCallback(() => {
    handleGoogleLogin(inviteToken, redirectTo);
  }, [handleGoogleLogin, inviteToken, redirectTo]);

  // by using a layout effect we prevent the user from seeing the first render
  useLayoutEffect(() => {
    if (logged) {
      handleLoggedIn(redirectTo);
    } else if (jwt && refresh) {
      handleLoginFromQuery({
        jwt,
        refresh,
        email,
        name,
        inviteToken,
      });
    }
  }, [logged, jwt, refresh, email, name, inviteToken, redirectTo, handleLoggedIn, handleLoginFromQuery]);

  useEffect(() => {
    if (error) {
      processErrorFromQuery(error);
    }
  }, [error]);

  const inviteMessage = getInviteMessage(inviteToken, redirectTo);
  const errorToDisplay = getFirstError(formik);
  const statusMessage = errorToDisplay || formik.status;
  const signUpUrl = `${SIGNUP_URL}${window.location.search}`;
  const forgotPasswordUrl = `${FORGOT_PASSWORD_URL}?email=${formik.values.email}`;

  return (
    <AuthPage
      additionalInfo={inviteMessage}
      ssoButton={
        <Button onClick={onGoogleLoginClick}>Sign in with Google</Button>
      }
      footerText={
        <>
          <p>
            <Link to={forgotPasswordUrl}>Forgot password?</Link>
          </p>
          <p>
            Don't have an account? <Link to={signUpUrl}>Sign up</Link>
          </p>
        </>
      }
    >
      <form className={cx('form')} onSubmit={formik.handleSubmit}>
        <Input
          {...formik.getFieldProps('email')}
          name="email"
          type="email"
          placeholder="Enter your email"
          error={getFieldError(formik, 'email')}
          autoFocus
        />
        <Input
          {...formik.getFieldProps('password')}
          name="password"
          type="password"
          placeholder="Enter your password"
          maxLength={512}
          error={getFieldError(formik, 'password')}
        />
        <div className={cx('buttonContainer')}>
          <p className={cx('statusMessage')}>
            {statusMessage || <>&nbsp;</>}
          </p>
          <Button type="submit" disabled={!canSubmit(formik)}>
            Sign in
          </Button>
        </div>
      </form>
    </AuthPage>
  );
}

export default memo(Login);
